import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Checkmark } from '@carbon/react/icons';
import { InlineNotification } from '@carbon/react';
import { useAtom } from 'jotai';
import type { ColumnFiltersState } from '@tanstack/react-table';

import {
  ColumnCheckboxGroupFilter,
  ColumnDateFilter,
  DataTable,
  FilterSection,
  type DataTableColumnDef,
} from 'components/data-table';
import { Link } from 'components/link/link';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import { SellersCell } from 'pages/packages/list/sellers-cell';

import { packageSourceSearchAtom, packageSourceSelectAllAtom } from '../atoms';
import { useSourceTableSelection } from '../hooks';
import { handleSourceNameClick, stopRowClick } from '../source-selection';
import type {
  PackageRow,
  PackageSelectAllRow,
  SourceRow,
  SourceSelectionChange,
} from '../types';
import {
  entitySellers,
  packageAcquirerToFormEntity,
  packageFilterStateToParams,
} from '../utils';

const DEFAULT_PAGE_SIZE = 25;

const CONTAINS_INVENTORY_OPTIONS = [
  { value: 'true', label: 'Contains Inventory' },
];

const INTEREST_TYPE_OPTIONS = [
  { value: 'working', label: 'Working interest' },
  { value: 'royalty', label: 'Royalty interest' },
];

/** Props for the SourcePackageTable component */
export interface SourcePackageTableProps {
  /** Every selected package id, across pages. The form owns this. */
  selectedIds: Set<number>;
  /** Fired with the resolved delta for both row clicks and select-all. */
  onSelectionChange: (change: SourceSelectionChange) => void;
  /** Whether the package tile is the one picked. */
  active: boolean;
}

/**
 * Project a row onto the form's source shape, carrying the seller and acquirer
 * provenance the wizard inherits from a source package. Both paths run through
 * here — a row read from the table and a row returned by `select_all` — so a
 * source picked in bulk behaves exactly like one picked by hand.
 *
 * @param {PackageSelectAllRow} row - Row from /queries/packages or its select_all
 * @return {SourceRow} Form-shape source row
 */
const toSourceRow = (row: PackageSelectAllRow): SourceRow => ({
  id: row.id,
  name: row.name,
  href: row.href,
  sellers: entitySellers(row.sellers).map((seller) => ({
    id: seller.id,
    name: seller.name,
  })),
  acquirers: row.acquirerEntities.map(packageAcquirerToFormEntity),
});

const packageColumns: DataTableColumnDef<PackageRow>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <Link
        to={row.original.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleSourceNameClick}
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    // Keeps the `sellerName` column id so sorting still sends the BE's
    // `sellerName` sort attribute.
    id: 'sellerName',
    accessorFn: (row) => row.sellers.map((seller) => seller.name).join(', '),
    header: 'Seller',
    // The seller links point at entities, not at this row's package, so
    // following one must not also toggle the row's selection.
    cell: ({ row }) => (
      <span role="presentation" onClick={stopRowClick}>
        <SellersCell sellers={row.original.sellers} />
      </span>
    ),
  },
  {
    id: 'acquirerEntities',
    // "Acquire Entity" is what this column is called everywhere else the same
    // data is tabulated — the packages list, the CRM related-packages tables,
    // the allocation-manager modal, the package show page. "Acquiring Entity"
    // is the Step 2 picker's label, which is a field, not this column.
    header: 'Acquire Entity',
    cell: ({ row }) => {
      const names = row.original.acquirerEntities.map((e) => e.name);
      return names.length > 0 ? names.join(', ') : '--';
    },
  },
  {
    id: 'closeDate',
    accessorKey: 'closeDate',
    header: 'Close Date',
    meta: { type: 'date' as const },
  },
  {
    id: 'totalAcreage',
    accessorKey: 'totalAcreage',
    header: 'Total Acreage',
    meta: { type: 'decimal' as const },
  },
  {
    id: 'slotCount',
    accessorKey: 'slotCount',
    header: 'Slots',
    meta: { type: 'integer' as const },
  },
  {
    id: 'effectiveDate',
    accessorKey: 'effectiveDate',
    header: 'Effective Date',
    meta: { type: 'date' as const },
  },
  {
    id: 'includeChildren',
    accessorKey: 'includeChildren',
    header: 'Include Children',
    cell: ({ row }) => (row.original.includeChildren ? <Checkmark /> : '--'),
  },
  {
    id: 'completedEvals',
    accessorKey: 'completedEvals',
    header: 'Completed Evals',
    meta: { type: 'integer' as const },
  },
  {
    id: 'legacyEvals',
    accessorKey: 'legacyEvals',
    header: 'Legacy Evals',
    meta: { type: 'integer' as const },
  },
  {
    id: 'workspaceCount',
    accessorKey: 'workspaceCount',
    header: 'Workspaces',
    meta: { type: 'integer' as const },
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Created At',
    meta: { type: 'date' as const },
  },
  // Hidden columns — rendered off-screen via `initialTableState.column-
  // Visibility` so the side filter panel's `Column*Filter` wrappers can
  // resolve them via `table.getColumn(columnId)`. `updatedAt`,
  // `containsInventoryAssets`, and `interestTypes` are real fields on
  // `PackageRow`; the design just doesn't surface them in the table grid.
  {
    id: 'updatedAt',
    accessorKey: 'updatedAt',
    header: 'Updated At',
    meta: { type: 'date' as const },
  },
  {
    id: 'containsInventoryAssets',
    accessorKey: 'containsInventoryAssets',
    header: 'Contains Inventory',
    meta: { type: 'enum' as const },
  },
  {
    id: 'interestTypes',
    accessorKey: 'interestTypes',
    // Singular, as every other interest-type column in the app labels it,
    // even though the underlying field is a list.
    header: 'Interest Type',
    meta: { type: 'enum' as const },
  },
];

const HIDDEN_COLUMNS = {
  updatedAt: false,
  containsInventoryAssets: false,
  interestTypes: false,
};

/**
 * Step 0 source table for existing packages. Multi-select: the checkbox, a
 * click anywhere on the row, and the name link all toggle one package, and the
 * header select-all covers the whole filtered match set via
 * `/queries/packages/select_all` (the ids the user just selected include rows
 * off the current page, which the table can't name from its own data).
 *
 * Server-paginated and server-searched via POST /queries/packages.
 *
 * @param {SourcePackageTableProps} props - Current selection and change handler
 * @return {JSX.Element} The package source table
 */
export const SourcePackageTable: FC<SourcePackageTableProps> = ({
  selectedIds,
  onSelectionChange,
  active,
}) => {
  const [{ data, loading, currentPayload }, setSearchPayload] = useAtom(
    packageSourceSearchAtom.searchAtom
  );

  // `searchAtomPair`'s writer fires a request on every call regardless of
  // payload identity, so the previous `(current) => current ?? defaults`
  // shape would refetch on every remount. Gate on `currentPayload` to seed
  // exactly once per atom lifetime.
  useEffect(() => {
    if (currentPayload === undefined) {
      setSearchPayload(() => ({ page: 1, pageSize: DEFAULT_PAGE_SIZE }));
    }
  }, [setSearchPayload, currentPayload]);

  // `page` / `pageSize` / `globalFilter` are consumed once by DataTable via
  // lazy-init `useState`, so snapshot them on first mount and never refresh.
  // `selection` is CONTROLLED (below) — it flows from `selectedIds` each render.
  const [initialPagination] = useState(() => ({
    page: currentPayload?.page,
    pageSize: currentPayload?.pageSize,
    globalFilter: currentPayload?.text,
  }));
  const initialTableState = useMemo<InitialTableState>(
    () => ({
      ...initialPagination,
      columnVisibility: HIDDEN_COLUMNS,
    }),
    [initialPagination]
  );

  // `packageSourceSearchAtom` is typed against the shared
  // `PackagesQueryResponse` (which types `id` as `string`); the jbuilder
  // emits the integer PK, so narrow to `PackageRow` for the column
  // accessors that expect `number`-valued fields.
  const rows = useMemo(
    () => (data?.results ?? []) as unknown as PackageRow[],
    [data?.results]
  );
  const totalCount = data?.totalCount ?? 0;
  const {
    controlledSelection,
    handleSelectionChange,
    isSelectingAll,
    selectAllError,
  } = useSourceTableSelection({
    selectAllAtom: packageSourceSelectAllAtom,
    payload: currentPayload,
    rows,
    totalCount,
    selectedIds,
    active,
    onSelectionChange,
    mapRow: toSourceRow,
  });

  const loadData = useCallback(
    ({ page, pageSize, globalFilter }: TableState) => {
      setSearchPayload((current) => ({
        ...current,
        page,
        pageSize,
        text: globalFilter || undefined,
      }));
    },
    [setSearchPayload]
  );

  // Filter changes invalidate the current page (results may shrink), so
  // reset to page 1 alongside the translated filter patch.
  const handleFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      setSearchPayload((current) => ({
        ...current,
        ...packageFilterStateToParams(filters),
        page: 1,
      }));
    },
    [setSearchPayload]
  );

  return (
    <>
      {selectAllError !== null && (
        <InlineNotification
          kind="error"
          title="Couldn't update the selection"
          subtitle={selectAllError}
          lowContrast
        />
      )}
      <DataTable<PackageRow>
        data={rows}
        columns={packageColumns}
        getRowId={(row) => String(row.id)}
        isLoading={loading || isSelectingAll}
        // Freeze selection while EITHER fetch is in flight, per the Step 1
        // tables' rule (`isSelectionDisabledFromStates`): mid-search
        // the controlled selection still describes the OLD page while
        // `selectAllParams` already carries the NEW filter, so a click in that
        // window can be misread as a header click — and a misread header click
        // here means "select every match". The stale-rows overlay only blocks
        // pointer events on `tbody`, so the header checkbox stays live without
        // this.
        disableSelection={isSelectingAll || loading}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount,
        }}
        onPaginationChange={loadData}
        onGlobalSearchChange={loadData}
        onSortChange={loadData}
        config={{
          selectionType: 'multi',
          rowClickSelection: true,
          globalSearch: true,
          columnFilters: true,
        }}
        initialTableState={initialTableState}
        controlledState={{ selection: controlledSelection }}
        searchPlaceholder="Search by Package Name"
        onSelectionChange={(s) => handleSelectionChange(s.selection)}
        onFilterChange={(s) => handleFilterChange(s.columnFilters)}
        renderFilterPanel={() => (
          <>
            <FilterSection title="Created Date">
              <ColumnDateFilter columnId="createdAt" />
            </FilterSection>
            <FilterSection title="Updated Date">
              <ColumnDateFilter columnId="updatedAt" />
            </FilterSection>
            <FilterSection title="Inventory">
              <ColumnCheckboxGroupFilter
                columnId="containsInventoryAssets"
                options={CONTAINS_INVENTORY_OPTIONS}
              />
            </FilterSection>
            <FilterSection title="Package Interest Types">
              <ColumnCheckboxGroupFilter
                columnId="interestTypes"
                options={INTEREST_TYPE_OPTIONS}
              />
            </FilterSection>
          </>
        )}
      />
    </>
  );
};
